import React from 'react';
import styled from 'styled-components';
import IconButton from '@mui/material/IconButton';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoIcon from '@mui/icons-material/Info';
import CloseIcon from '@mui/icons-material/Close';

export type BannerVariant = 'danger' | 'warning' | 'success' | 'neutral' | 'disabled';

export type BannerContent = string | React.ComponentType;

export interface BannerProps {
  variant: BannerVariant;
  content: BannerContent;
  onClose?: () => void;
}

const getVariantStyles = (variant: BannerVariant): { backgroundColor: string; borderColor: string } => {
  switch (variant) {
    case 'danger':
      return {
        backgroundColor: '#fae6e7',
        borderColor: '#b60009'
      };
    case 'warning':
      return {
        backgroundColor: '#fef7e9',
        borderColor: '#aa7f19'
      };
    case 'success':
      return {
        backgroundColor: '#f0f7ea',
        borderColor: '#4b7f20'
      };
    case 'neutral':
      return {
        backgroundColor: '#e6f7ff',
        borderColor: '#005e8e'
      };
    case 'disabled':
      return {
        backgroundColor: '#f5f5f5',
        borderColor: '#212121'
      };
    default:
      return {
        backgroundColor: '#f5f5f5',
        borderColor: '#212121'
      };
  }
};

const getVariantIcon = (variant: BannerVariant): React.ReactElement => {
  switch (variant) {
    case 'danger':
      return <ErrorOutlineIcon />;
    case 'warning':
      return <WarningAmberIcon />;
    case 'success':
      return <CheckCircleIcon />;
    case 'neutral':
    case 'disabled':
      return <InfoIcon />;
    default:
      return <InfoIcon />;
  }
};

const BannerContainer = styled.div<{ $variant: BannerVariant }>`
  border: 1px solid ${({ $variant }): string => getVariantStyles($variant).borderColor};
  background-color: ${({ $variant }): string => getVariantStyles($variant).backgroundColor};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 16px;
  gap: 10px;
`;

const InformationSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
`;

const IconTextSection = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  color: #212121;
`;

const TextWrapper = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-weight: 400;
  font-size: 16px;
  line-height: 1.47;
  color: #212121;
`;

const CloseButtonSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-shrink: 0;
`;

const StyledIconButton = styled(IconButton)`
  width: 36px;
  height: 36px;
  padding: 0;
  color: #212121;

  &:hover {
    background-color: rgba(33, 33, 33, 0.08);
  }
`;

const CloseIconWrapper = styled.div`
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const Banner: React.FC<BannerProps> = ({ variant, content, onClose }) => {
  const icon = getVariantIcon(variant);

  const renderContent = (): React.ReactNode => {
    if (typeof content === 'string') {
      return content;
    }
    const ContentComponent = content;
    return <ContentComponent />;
  };

  return (
    <BannerContainer $variant={variant}>
      <InformationSection>
        <IconTextSection>
          <IconWrapper>{icon}</IconWrapper>
          <TextWrapper>{renderContent()}</TextWrapper>
          {onClose && (
            <CloseButtonSection>
              <StyledIconButton onClick={onClose} size="small" aria-label="Close banner">
                <CloseIconWrapper>
                  <CloseIcon />
                </CloseIconWrapper>
              </StyledIconButton>
            </CloseButtonSection>
          )}
        </IconTextSection>
      </InformationSection>
    </BannerContainer>
  );
};
